import React, { useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Collapse, Dialog, DialogContent, IconButton, InputAdornment,
  Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';

const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;
const DAY_ABBR = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];

const CELL = { py: 0.6, px: 1, fontSize: '0.78rem', whiteSpace: 'nowrap' };
const HEAD = { ...CELL, fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' };
const NUM = { ...CELL, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const FORMULA = { fontSize: '0.74rem', color: '#64748b' };

function Section({ no, title, children, hint }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', mb: 0.5 }}>
        {no}) {title}
      </Typography>
      {hint && <Typography sx={{ ...FORMULA, mb: 1 }}>{hint}</Typography>}
      {children}
    </Box>
  );
}

// 1 บรรทัดของสูตรสรุป: ชื่อรายการ | วิธีคิด | ค่า
function Line({ label, formula, value, sign, bold, color, manual }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ py: 0.35, borderBottom: '1px dashed #e2e8f0' }}>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: bold ? 700 : 500, minWidth: 150, color: '#334155' }}>
        {label}
        {manual && <Chip size="small" label="HR แก้เอง" sx={{ ml: 0.75, height: 16, fontSize: '0.62rem', bgcolor: '#fef3c7', color: '#92400e' }} />}
      </Typography>
      <Typography sx={{ ...FORMULA, flex: 1, whiteSpace: 'pre-line' }}>{formula}</Typography>
      <Typography sx={{ fontSize: '0.86rem', fontWeight: bold ? 800 : 600, color: color || '#0f172a', minWidth: 110, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {sign}{fmt(value)}
      </Typography>
    </Stack>
  );
}

const dayLabel = (r) => `${r.date.format('DD/MM')} ${DAY_ABBR[r.dow]}`;
const timeRange = (r) => (r.checkInTime ? `${r.checkInTime}–${r.checkOutTime || '?'}` : '—');

// ── หลักการคิดโดยรวม ──
function RulesCard({ isWorkTime }) {
  const items = [
    ['ค่าแรงต่อชั่วโมง', 'ค่าแรงต่อวัน ÷ 8 ชั่วโมง'],
    [
      'พนักงานรายวัน — ค่าแรง',
      isWorkTime
        ? 'โหมดปัจจุบัน "ตามชั่วโมงทำงานจริง": แต่ละวัน = (ชม.ทำงานจริง ÷ 8) × เรตวันนั้น (วันหยุด ×2 ฯลฯ) × ค่าแรงต่อวัน — ชม.จริงนับ 08:00–17:00 หักพักเที่ยง 1 ชม. (เข้าหลังเที่ยงนับตั้งแต่ 13:00) ถ้า HR แก้ชั่วโมง/ค่าแรงวันนั้นจะใช้ค่าที่แก้'
        : 'โหมดปัจจุบัน "ตามวัน": แต่ละวัน = (8 ชม. ถ้าเข้าเช้า / 4 ชม. ถ้าเข้าบ่ายหรือลาครึ่งวัน ÷ 8) × เรตวันนั้น × ค่าแรงต่อวัน',
    ],
    ['พนักงานรายเดือน — ค่าแรง', 'ค่าแรงต่อวัน × 30 − (วันลาไม่ได้ค่าแรง + วันที่ติ๊กไม่ได้ค่าแรง) × ค่าแรงต่อวัน + (ส่วนเกินเรตวันหยุด) × ค่าแรงต่อวัน — ไม่ขึ้นกับจำนวนวันที่ลงเวลา'],
    ['วันลา / เพิ่มชั่วโมงเอง', 'ลาที่ให้ค่าแรง = (ชม.ลา ÷ 8) × ค่าแรงต่อวัน (เต็มวัน 8 ชม. / ครึ่งวัน 4 ชม.) · เพิ่มชั่วโมงเอง = (ชม. ÷ 8) × เรต × ค่าแรงต่อวัน หรือยอดที่ HR กำหนด'],
    ['OT', 'ชม. OT × เรต OT (ปกติ 1.5) × ค่าแรงต่อชม.ของวันนั้น — OT ที่อนุมัติ ใช้ชม.ที่คิดเรตแล้ว × ค่าแรงต่อชม. · ถ้า HR กรอก OT เอง ใช้ค่าที่กรอก'],
    ['ค่าออกสนาม', 'เฉพาะคนที่มีสิทธิ์ และวันที่ถูกติ๊กเป็นวันออกสนาม: (ชม.ทำงาน สูงสุด 8) × (อัตราค่าออกสนาม/วัน ÷ 8) + ชม. OT × เรต OT × (อัตราค่าออกสนาม/วัน ÷ 8)'],
    ['เบี้ยเลี้ยง', 'ตามอัตราเบี้ยเลี้ยงต่อวันของวันที่ทำงานจริง (extra_day / ที่ HR กำหนด)'],
    ['ประกันสังคม', 'ฐานค่าแรง × อัตรา% (ปกติ 5%) — ขั้นต่ำ 83 และสูงสุด 875 บาทต่อเดือน (นับรวมที่หักไปแล้วในงวดก่อนของเดือนเดียวกัน)'],
    ['ภาษี', 'ค่าแรงต่อวัน × จำนวนวันทำงาน × อัตราภาษี% (เฉพาะคนที่เปิดใช้)'],
    ['ที่ต้องจ่าย', 'ค่าแรง + OT + เบี้ยเลี้ยง + ค่าออกสนาม + เงินได้อื่นๆ − ภาษี − ประกันสังคม − หัก CRL − หักอื่นๆ ± ยอดยกมา (ค่าแรงที่ใช้ในช่องนี้คิดตามชม.จริงเสมอ)'],
    [
      'กระจายลงโครงการ — รายวัน',
      'ค่าแรง/OT/ค่าออกสนาม/เบี้ยเลี้ยงที่ผูกกับวันที่ลงเวลา ลงโครงการของวันนั้นตรงๆ · ส่วนที่ไม่ผูกวัน (เงินได้อื่นๆ − ปกส. − ภาษี ± ส่วนต่าง/ยอดยกมา) กระจายตามสัดส่วนรายได้ต่อโครงการ · หัก CRL และหักอื่นๆ ลงเต็มก้อนที่โครงการที่ลงเวลามากที่สุด',
    ],
    [
      'กระจายลงโครงการ — รายเดือน',
      'ค่าแรงเต็มเดือน, เงินได้อื่นๆ, ปกส., ภาษี, CRL, หักอื่นๆ แบ่งตาม % ชั่วโมงที่ลงแต่ละโครงการ · OT/เบี้ยเลี้ยง/ค่าออกสนามลงโครงการที่ลงเวลาจริงของวันนั้นตรงๆ',
    ],
  ];
  const [open, setOpen] = useState(false); // ซ่อนไว้ก่อน กดหัวการ์ดเพื่อเปิด/ปิด
  return (
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 2, bgcolor: '#fcfcfd', overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        onClick={() => setOpen((v) => !v)}
        sx={{ px: 1.5, py: 1, cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: '#f8fafc' } }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', flex: 1 }}>หลักการคิดโดยรวม</Typography>
        <Typography sx={{ ...FORMULA, mr: 0.5 }}>{open ? 'ซ่อน' : 'แสดง'}</Typography>
        <ExpandMoreIcon sx={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', color: '#64748b' }} />
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {items.map(([k, v], i) => (
            <Stack key={k} direction="row" spacing={1} sx={{ py: 0.35 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, minWidth: 190, color: '#334155' }}>{i + 1}. {k}</Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#475569', flex: 1 }}>{v}</Typography>
            </Stack>
          ))}
          <Typography sx={{ ...FORMULA, mt: 1 }}>
            ตัวเลขทุกตัวในหน้านี้ดึงจากชุดเดียวกับตารางเงินเดือนและตารางต้นทุนโครงการ จึงตรงกันเสมอ (งวดที่ล็อกแล้ว ยอดที่บันทึกอาจต่างจากหน้านี้ถ้ามีการแก้ข้อมูลภายหลัง)
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}

// ── ค่าแรง (รายวัน) ──
function DailyWageTable({ e }) {
  const rows = e.dayRows;
  if (!rows.length) return <Typography sx={FORMULA}>ไม่มีวันที่ลงเวลา/เพิ่มชั่วโมง/ลาในงวดนี้</Typography>;
  const wageOf = (r) => (e.isWorkTime ? r.actualWage : r.wage);
  return (
    <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={HEAD}>วัน</TableCell>
            <TableCell sx={HEAD}>โครงการ</TableCell>
            <TableCell sx={HEAD}>เข้า–ออก</TableCell>
            <TableCell sx={HEAD}>วิธีคิดค่าแรงวันนั้น</TableCell>
            <TableCell sx={{ ...HEAD, textAlign: 'right' }}>ค่าแรง</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const ck = r.parts?.ck;
            const pieces = [];
            if (ck) {
              const h = e.isWorkTime ? ck.actualH : ck.displayH;
              const override = e.isWorkTime ? ck.actualOverride : ck.displayOverride;
              if (override) {
                const dr = e.isWorkTime ? ck.dayRate : ck.displayDayRate;
                const rt = e.isWorkTime ? ck.rate : ck.displayRate;
                pieces.push(`(${h} ชม. ÷ 8) × [เรต ${rt} × ค่าแรง/วันที่ HR แก้ ${fmt(dr)}] = ${fmt(e.isWorkTime ? ck.actualAmt : ck.displayAmt)}`);
              } else {
                const rt = e.isWorkTime ? ck.rate : ck.displayRate;
                pieces.push(`(${h} ชม. ÷ 8) × เรต ${rt} × ${fmt(e.spd)} = ${fmt(e.isWorkTime ? ck.actualAmt : ck.displayAmt)}`);
              }
            }
            if (r.parts?.manual) {
              const m = r.parts.manual;
              pieces.push(`+ เพิ่มชม. ${m.hours} ชม.${m.fixedAmount ? ' (ยอดที่ HR กำหนด)' : ` × เรต ${m.rate}`} = ${fmt(m.amt)} (โดย ${m.by})`);
            }
            if (r.parts?.leave) {
              pieces.push(`+ ลา ${r.parts.leave.hours} ชม. = (${r.parts.leave.hours} ÷ 8) × ${fmt(e.spd)} = ${fmt(r.parts.leave.amt)}`);
            }
            const flags = (r.notes || []).filter((n) => ['late', 'nocheckout', 'skip_late', 'holiday', 'afternoon'].includes(n.type)).map((n) => n.text);
            return (
              <TableRow key={r.d} hover>
                <TableCell sx={CELL}>{dayLabel(r)}</TableCell>
                <TableCell sx={{ ...CELL, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.project || ''}>{r.projectNumber || r.project || '—'}</TableCell>
                <TableCell sx={CELL}>{timeRange(r)}</TableCell>
                <TableCell sx={{ ...CELL, whiteSpace: 'normal', minWidth: 280 }}>
                  {pieces.map((p) => <div key={p} style={{ fontSize: '0.74rem' }}>{p}</div>)}
                  {flags.length > 0 && <div style={{ fontSize: '0.68rem', color: '#b45309' }}>{flags.join(' · ')}</div>}
                </TableCell>
                <TableCell sx={NUM}>{fmt(wageOf(r))}</TableCell>
              </TableRow>
            );
          })}
          <TableRow>
            <TableCell colSpan={4} sx={{ ...CELL, fontWeight: 700 }}>รวมจากรายวัน ({rows.length} วัน)</TableCell>
            <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(e.isWorkTime ? e.dailySumActual : e.dailySumDisplay)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function FulltimeWage({ e }) {
  const s = e.summary;
  const base = e.spd * 30;
  const unpaid = s.unpaidLeaveDays || 0;
  const extra = s.rateExtraDays || 0;
  return (
    <Box>
      <Line label="เงินเดือนเต็ม" formula={`ค่าแรง/วัน ${fmt(e.spd)} × 30 วัน`} value={base} />
      <Line
        label="หักวันไม่ได้ค่าแรง"
        formula={`${unpaid} วัน × ${fmt(e.spd)}${e.unpaidRows.length ? `\n${e.unpaidRows.map((r) => `${dayLabel(r)}: ${r.noWageText}`).join('\n')}` : ''}`}
        value={unpaid * e.spd}
        sign="−"
        color="#b91c1c"
      />
      <Line
        label="บวกส่วนเกินเรตวันหยุด"
        formula={`ผลรวม (เรต − 1) = ${extra} × ${fmt(e.spd)}${e.rateExtraRows.length ? `\n${e.rateExtraRows.map((r) => `${dayLabel(r)}: เรต ${r.parts.ck.rate} → +${r.extraRate}`).join('\n')}` : ''}`}
        value={extra * e.spd}
        sign="+"
        color="#15803d"
      />
      <Line label="ค่าแรง (รายเดือน)" formula="ไม่ต่ำกว่า 0" value={e.actualSalary} bold />
    </Box>
  );
}

// ── OT ──
function OtTable({ e }) {
  const rows = e.ot.rows || [];
  if (!rows.length) return <Typography sx={FORMULA}>ไม่มี OT ในงวดนี้</Typography>;
  return (
    <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={HEAD}>วัน</TableCell>
            <TableCell sx={HEAD}>ที่มา</TableCell>
            <TableCell sx={HEAD}>วิธีคิด</TableCell>
            <TableCell sx={{ ...HEAD, textAlign: 'right' }}>OT</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.day} hover>
              <TableCell sx={CELL}>วันที่ {r.day}</TableCell>
              <TableCell sx={CELL}>
                {r.isManualOt ? 'HR กรอกเอง' : 'OT ที่อนุมัติ'}
                {r.isOverride ? ' (ทับ OT ที่อนุมัติ)' : ''}
              </TableCell>
              <TableCell sx={{ ...CELL, whiteSpace: 'normal' }}>
                {r.isManualOt
                  ? `${r.otHours} ชม. × เรต ${r.otRate} × ค่าแรง/ชม. ${fmt(r.dayHrR)}`
                  : `${r.otHours} ชม. (เรตเฉลี่ย ${r.otRate}) × ค่าแรง/ชม. ${fmt(r.dayHrR)}`}
              </TableCell>
              <TableCell sx={NUM}>{fmt(r.otAmt)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={3} sx={{ ...CELL, fontWeight: 700 }}>รวม OT</TableCell>
            <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(e.ot.total)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function FieldAllowanceTable({ e }) {
  if (!e.fa.faEnabled) return <Typography sx={FORMULA}>พนักงานคนนี้ไม่มีสิทธิ์รับค่าออกสนาม</Typography>;
  const rows = e.fa.rows || [];
  if (!rows.length) return <Typography sx={FORMULA}>มีสิทธิ์ ({fmt(e.fa.faDay)} บาท/วัน) แต่ไม่มีวันที่ถูกติ๊กออกสนามในงวดนี้</Typography>;
  return (
    <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={HEAD}>วัน</TableCell>
            <TableCell sx={HEAD}>วิธีคิด (อัตรา {fmt(e.fa.faDay)}/วัน → {fmt(e.fa.faHrRate)}/ชม.)</TableCell>
            <TableCell sx={{ ...HEAD, textAlign: 'right' }}>ค่าออกสนาม</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.day} hover>
              <TableCell sx={CELL}>วันที่ {r.day}</TableCell>
              <TableCell sx={{ ...CELL, whiteSpace: 'normal' }}>
                {`${r.faHours} ชม. × ${fmt(r.faHrRate)}`}
                {r.otHours > 0 ? ` + OT ${r.otHours} ชม. × ${r.otRate} × ${fmt(r.faHrRate)}` : ''}
              </TableCell>
              <TableCell sx={NUM}>{fmt(r.amt)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={2} sx={{ ...CELL, fontWeight: 700 }}>รวมค่าออกสนาม</TableCell>
            <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(e.fa.total)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// แถวต่อโครงการของพนักงาน 1 คน + สัดส่วนที่ใช้แบ่ง + ส่วนต่าง (ใช้ร่วมกันทั้งตารางรายคนและมุมมองรายโครงการ)
function computeProjectRows(e) {
  return e.projects.map((p) => {
    const share = e.isFulltime
      ? (p.ft?.share ?? 0)
      : (e.grossSum > 0 ? p.gross / e.grossSum : (p.isDominant ? 1 : 0));
    const base = p.gross + p.bonus - p.sso - p.tax - p.crl - p.other;
    return { ...p, share, adj: p.payable - base };
  });
}

// ── การกระจายลงโครงการ ──
function ProjectDistribution({ e }) {
  if (!e.projects.length) return <Typography sx={FORMULA}>ไม่มียอดที่ลงโครงการ</Typography>;
  const rows = computeProjectRows(e);
  const tot = (k) => rows.reduce((s, r) => s + (r[k] || 0), 0);
  const COLS = [
    ['salary', 'ค่าแรง'], ['ot', 'OT'], ['fa', 'ออกสนาม'], ['allowance', 'เบี้ยเลี้ยง'],
    ['bonus', '+เงินได้อื่นๆ'], ['sso', '−ปกส.'], ['tax', '−ภาษี'], ['crl', '−CRL'], ['other', '−หักอื่นๆ'], ['adj', '± ส่วนต่าง/ยอดยกมา'],
  ];
  return (
    <Box>
      <Typography sx={{ ...FORMULA, mb: 1, whiteSpace: 'pre-line' }}>
        {e.isFulltime
          ? `พนักงานรายเดือน: ค่าแรงเต็มเดือน ${fmt(e.actualSalary)} แบ่งตาม % ชั่วโมง (รวม ${fmt(e.ftSplit?.totalHours || 0)} ชม.) · OT/เบี้ยเลี้ยง/ค่าออกสนามลงตามโครงการของวันนั้นตรงๆ (รวม ${fmt(e.grossSum)})\nส่วนที่เหลือ = ที่ต้องจ่าย ${fmt(e.payable)} − ยอดที่ลงตรง ${fmt(e.grossSum)} = ${fmt(e.remainder)} แบ่งตาม % ชั่วโมง (รวมเงินได้อื่นๆ ปกส. ภาษี CRL หักอื่นๆ ยอดยกมา)`
          : `พนักงานรายวัน: ยอดที่ผูกกับวันลงเวลาลงโครงการของวันนั้นตรงๆ (รวม ${fmt(e.grossSum)})\nส่วนที่เหลือ = ที่ต้องจ่าย ${fmt(e.payable)} + CRL ${fmt(e.deductions.crl)} + หักอื่นๆ ${fmt(e.deductions.other)} − ยอดที่ลงตรง ${fmt(e.grossSum)} = ${fmt(e.remainder)} กระจายตามสัดส่วนรายได้ต่อโครงการ · CRL และหักอื่นๆ หักเต็มก้อนที่โครงการ ★ (ลงเวลามากสุด)`}
        {e.carryOut > 0 ? `\nยอดติดลบที่ยกไปงวดหน้า ${fmt(e.carryOut)} บวกคืนที่โครงการ ★` : ''}
        {Math.abs(e.tableVsDaily) > 0.05 ? `\nหมายเหตุ: ยอดรายได้ในตาราง (ค่าแรง+OT+เบี้ยเลี้ยง+ค่าออกสนาม) ต่างจากยอดที่ลงโครงการจากรายวัน ${fmt(e.tableVsDaily)} (HR แก้ตัวเลขในตาราง) — ส่วนต่างนี้อยู่ในคอลัมน์ "± ส่วนต่าง"` : ''}
      </Typography>
      <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD}>โครงการ</TableCell>
              <TableCell sx={HEAD}>{e.isFulltime ? 'ชม. / สัดส่วน' : 'วัน / สัดส่วนรายได้'}</TableCell>
              {COLS.map(([k, l]) => <TableCell key={k} sx={{ ...HEAD, textAlign: 'right' }}>{l}</TableCell>)}
              <TableCell sx={{ ...HEAD, textAlign: 'right' }}>= ที่ต้องจ่าย</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key} hover>
                <TableCell sx={{ ...CELL, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.name}>
                  {r.number || r.name || 'ไม่ระบุโครงการ'}{r.isDominant ? ' ★' : ''}
                </TableCell>
                <TableCell sx={CELL}>
                  {e.isFulltime
                    ? `${fmt(r.ft?.hours || 0)} ชม. · ${pct(r.share)}`
                    : `${r.daysInProject ?? '-'} วัน · ${pct(r.share)}`}
                </TableCell>
                {COLS.map(([k]) => <TableCell key={k} sx={NUM}>{Math.abs(r[k] || 0) < 0.005 ? '—' : fmt(r[k])}</TableCell>)}
                <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(r.payable)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2} sx={{ ...CELL, fontWeight: 700 }}>รวมทุกโครงการ</TableCell>
              {COLS.map(([k]) => <TableCell key={k} sx={{ ...NUM, fontWeight: 700 }}>{fmt(tot(k))}</TableCell>)}
              <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(tot('payable'))}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography sx={{ ...FORMULA, mt: 0.5 }}>
        ตรวจผล: ผลรวมโครงการ {fmt(tot('payable'))} = ที่ต้องจ่าย {fmt(e.payable)}{e.carryOut > 0 ? ` + ยอดยกไป ${fmt(e.carryOut)}` : ''} = {fmt(e.payable + (e.carryOut || 0))}
      </Typography>
    </Box>
  );
}

function EmployeeBody({ e }) {
  const isManual = (k) => e.manualFields.includes(k);
  const incTotal = e.incomes.salary + e.incomes.overtime + e.incomes.allowances + e.incomes.fieldAllowance + e.incomes.bonus;
  const dedTotal = e.deductions.tax + e.deductions.sso + e.deductions.crl + e.deductions.other;
  const diffOt = Math.abs(e.incomes.overtime - e.fromDaily.overtime) > 0.05;
  const diffAllow = Math.abs(e.incomes.allowances - e.fromDaily.allowances) > 0.05;
  const diffFa = Math.abs(e.incomes.fieldAllowance - e.fromDaily.fieldAllowance) > 0.05;
  return (
    <Box>
      <Section no="1" title={`ค่าแรง (${e.isFulltime ? 'รายเดือน' : 'รายวัน'}) — ค่าแรง/วัน ${fmt(e.spd)} → ต่อชม. ${fmt(e.hourRate)}`}>
        {e.isFulltime ? <FulltimeWage e={e} /> : (
          <>
            <DailyWageTable e={e} />
            <Line
              label="ค่าแรงที่ใช้คิดเงินสุทธิ"
              formula={`ตามชม.จริง (ใช้ในช่อง "ที่ต้องจ่าย")${Math.abs(e.dailySumActual - e.actualSalary) > 0.05 ? `\nต่างจากผลรวมรายวัน ${fmt(e.dailySumActual)} เล็กน้อย เพราะวันลาครึ่งวันคิดชั่วโมงสูงสุด 4 ชม.` : ''}`}
              value={e.actualSalary}
              bold
            />
            {!e.isWorkTime && <Line label="ค่าแรงตามวัน (ฐานประกันสังคม)" formula="โหมด 'ตามวัน' ใช้เป็นฐานคิดประกันสังคม" value={e.displaySalary} />}
          </>
        )}
      </Section>

      <Section no="2" title="OT" hint="OT = ชม. OT × เรต OT × ค่าแรงต่อชม.ของวันนั้น">
        <OtTable e={e} />
      </Section>

      <Section no="3" title="ค่าออกสนาม">
        <FieldAllowanceTable e={e} />
      </Section>

      <Section no="4" title="เบี้ยเลี้ยง">
        {e.dayRows.filter((r) => r.allowance > 0).length === 0
          ? <Typography sx={FORMULA}>ไม่มีเบี้ยเลี้ยง</Typography>
          : (
            <Typography sx={{ ...FORMULA, whiteSpace: 'pre-line' }}>
              {e.dayRows.filter((r) => r.allowance > 0).map((r) => `${dayLabel(r)}: ${fmt(r.allowance)}`).join('\n')}
              {`\nรวม ${fmt(e.fromDaily.allowances)}`}
            </Typography>
          )}
      </Section>

      <Section no="5" title="สรุปรายได้ − รายการหัก = ที่ต้องจ่าย" hint="ตัวเลขในช่องที่ HR แก้เองในตารางจะมีป้าย 'HR แก้เอง' และใช้ค่านั้นแทนค่าที่คำนวณอัตโนมัติ">
        <Line label="ค่าแรง" formula={e.isFulltime ? 'จากข้อ 1 (รายเดือน)' : 'จากข้อ 1 (ตามชม.จริง)'} value={e.incomes.salary} sign="+" />
        <Line label="OT" formula={diffOt ? `คำนวณจากรายวัน ${fmt(e.fromDaily.overtime)} แต่ในตารางเป็นค่าที่แก้` : 'จากข้อ 2'} value={e.incomes.overtime} sign="+" manual={isManual('overtime')} />
        <Line label="เบี้ยเลี้ยง" formula={diffAllow ? `คำนวณจากรายวัน ${fmt(e.fromDaily.allowances)} แต่ในตารางเป็นค่าที่แก้` : 'จากข้อ 4'} value={e.incomes.allowances} sign="+" manual={isManual('allowances')} />
        <Line label="ค่าออกสนาม" formula={diffFa ? `คำนวณจากรายวัน ${fmt(e.fromDaily.fieldAllowance)} แต่ในตารางเป็นค่าที่แก้` : 'จากข้อ 3'} value={e.incomes.fieldAllowance} sign="+" manual={isManual('field_allowance')} />
        <Line label="เงินได้อื่นๆ" formula="HR กรอก (โบนัส/เงินได้อื่น)" value={e.incomes.bonus} sign="+" manual={isManual('bonus')} />
        <Line label="รวมรายได้" formula="" value={incTotal} bold />

        <Line
          label="ประกันสังคม"
          formula={
            e.sso.enabled
              ? `ฐาน ${fmt(e.sso.base)} × ${e.sso.rate}% = ${fmt(e.sso.raw)}${e.sso.paid > 0 ? `\nหักแล้วงวดก่อน ${fmt(e.sso.paid)} (รวมไม่เกิน 875)` : ''}\nขั้นต่ำ 83 / สูงสุด 875 บาทต่อเดือน → ได้ ${fmt(e.sso.calc)}`
              : 'ไม่ได้เปิดใช้ประกันสังคม'
          }
          value={e.deductions.sso}
          sign="−"
          color="#b91c1c"
          manual={isManual('sso')}
        />
        <Line
          label="ภาษี"
          formula={e.tax.cfg?.status ? `${fmt(e.spd)} × ${e.tax.days} วัน × ${e.tax.cfg.number}% = ${fmt(e.tax.auto)}` : 'ไม่ได้เปิดใช้หักภาษี'}
          value={e.deductions.tax}
          sign="−"
          color="#b91c1c"
          manual={isManual('tax')}
        />
        <Line label="หัก CRL" formula="ยอดเบิกเงินเดือนล่วงหน้า (CRL) ที่อนุมัติแล้วในงวดนี้" value={e.deductions.crl} sign="−" color="#b91c1c" />
        <Line label="หักอื่นๆ" formula="HR กรอก / หักสาย / ยอดยกมาที่ลงช่องนี้" value={e.deductions.other} sign="−" color="#b91c1c" manual={isManual('other')} />
        <Line label="รวมรายการหัก" formula="" value={dedTotal} sign="−" bold color="#b91c1c" />
        {Math.abs(e.carryDelta) > 0.005 && (
          <Line
            label="ยอดยกมาจากงวดก่อน"
            formula={`บวก ${fmt(e.carry.addTotal)} − ลบ ${fmt(e.carry.deductTotal)}`}
            value={e.carryDelta}
            sign={e.carryDelta >= 0 ? '+' : ''}
          />
        )}
        <Line
          label="ที่ต้องจ่าย"
          formula={`${fmt(incTotal)} − ${fmt(dedTotal)}${Math.abs(e.carryDelta) > 0.005 ? ` ${e.carryDelta >= 0 ? '+' : '−'} ${fmt(Math.abs(e.carryDelta))}` : ''}`}
          value={e.payable}
          bold
          color="#c2410c"
        />
      </Section>

      <Section no="6" title="กระจายลงโครงการ">
        <ProjectDistribution e={e} />
      </Section>
    </Box>
  );
}

// ── มุมมองรายโครงการ: ยอดของโครงการนี้มาจากใครกี่บาท พร้อมสูตรของแต่ละคน ──
function ProjectFocus({ data, focus }) {
  const items = [];
  data.employees.forEach((e) => {
    const pr = computeProjectRows(e).find((r) => r.key === focus.key && !!r.external === !!focus.external);
    if (pr) items.push({ e, pr });
  });
  items.sort((a, b) => b.pr.payable - a.pr.payable);
  const total = items.reduce((s, x) => s + x.pr.payable, 0);
  const COLS = [['salary', 'ค่าแรง'], ['ot', 'OT'], ['fa', 'ออกสนาม'], ['allowance', 'เบี้ยเลี้ยง'], ['bonus', '+เงินได้อื่นๆ'], ['sso', '−ปกส.'], ['tax', '−ภาษี'], ['crl', '−CRL'], ['other', '−หักอื่นๆ'], ['adj', '± ส่วนต่าง/ยอดยกมา']];
  if (!items.length) return <Typography sx={FORMULA}>ไม่พบยอดของโครงการนี้ในงวดที่เลือก</Typography>;
  return (
    <Box>
      <Typography sx={{ ...FORMULA, mb: 1 }}>
        ยอดที่ต้องจ่ายของโครงการ = ผลรวมของแต่ละคนด้านล่าง · แต่ละคน = ยอดที่ผูกกับวันที่ลงเวลาที่โครงการนี้ + ส่วนที่แบ่งมาตามสัดส่วน (ดูกติกาในหลักการคิดโดยรวม)
      </Typography>
      <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD}>พนักงาน</TableCell>
              <TableCell sx={HEAD}>วัน/ชม. · สัดส่วน</TableCell>
              {COLS.map(([k, l]) => <TableCell key={k} sx={{ ...HEAD, textAlign: 'right' }}>{l}</TableCell>)}
              <TableCell sx={{ ...HEAD, textAlign: 'right' }}>= ที่ต้องจ่าย</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(({ e, pr }) => (
              <TableRow key={e.id} hover>
                <TableCell sx={CELL}>{e.name} <span style={{ color: '#94a3b8' }}>({e.isFulltime ? 'รายเดือน' : 'รายวัน'})</span>{pr.isDominant ? ' ★' : ''}</TableCell>
                <TableCell sx={CELL}>{e.isFulltime ? `${fmt(pr.ft?.hours || 0)} ชม.` : `${pr.daysInProject ?? '-'} วัน`} · {pct(pr.share)}</TableCell>
                {COLS.map(([k]) => <TableCell key={k} sx={NUM}>{Math.abs(pr[k] || 0) < 0.005 ? '—' : fmt(pr[k])}</TableCell>)}
                <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(pr.payable)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2} sx={{ ...CELL, fontWeight: 700 }}>รวมโครงการ</TableCell>
              {COLS.map(([k]) => <TableCell key={k} sx={{ ...NUM, fontWeight: 700 }}>{fmt(items.reduce((s, x) => s + (x.pr[k] || 0), 0))}</TableCell>)}
              <TableCell sx={{ ...NUM, fontWeight: 700, color: '#c2410c' }}>{fmt(total)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>รายละเอียดสูตรของแต่ละคนในโครงการนี้</Typography>
      {items.map(({ e, pr }) => {
        const days = e.dayRows.filter((r) => (r.projectId || 'no_project') === focus.key);
        const remainderPart = e.remainder * pr.share;
        return (
          <Accordion key={e.id} disableGutters defaultExpanded sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', '&:before': { display: 'none' }, mb: 1, borderRadius: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', pr: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{e.name}</Typography>
                <Chip size="small" label={e.isFulltime ? 'รายเดือน' : 'รายวัน'} sx={{ height: 20, fontSize: '0.7rem' }} />
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontWeight: 800, color: '#c2410c' }}>{fmt(pr.payable)} บาท</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ borderTop: '1px solid #f1f5f9' }}>
              {days.length > 0 && (
                <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, mb: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={HEAD}>วัน</TableCell>
                        <TableCell sx={HEAD}>เข้า–ออก</TableCell>
                        {!e.isFulltime && <TableCell sx={HEAD}>วิธีคิดค่าแรง</TableCell>}
                        {!e.isFulltime && <TableCell sx={{ ...HEAD, textAlign: 'right' }}>ค่าแรง</TableCell>}
                        <TableCell sx={{ ...HEAD, textAlign: 'right' }}>OT</TableCell>
                        <TableCell sx={{ ...HEAD, textAlign: 'right' }}>ค่าออกสนาม</TableCell>
                        <TableCell sx={{ ...HEAD, textAlign: 'right' }}>เบี้ยเลี้ยง</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {days.map((r) => {
                        const ck = r.parts?.ck;
                        return (
                          <TableRow key={r.d} hover>
                            <TableCell sx={CELL}>{dayLabel(r)}</TableCell>
                            <TableCell sx={CELL}>{timeRange(r)}</TableCell>
                            {!e.isFulltime && (
                              <TableCell sx={{ ...CELL, whiteSpace: 'normal' }}>
                                {ck ? `(${ck.actualH} ชม. ÷ 8) × เรต ${ck.rate} × ${fmt(ck.actualOverride ? ck.dayRate : e.spd)}` : '—'}
                              </TableCell>
                            )}
                            {!e.isFulltime && <TableCell sx={NUM}>{fmt(ck?.actualAmt || 0)}</TableCell>}
                            <TableCell sx={NUM}>{r.otAmt ? fmt(r.otAmt) : '—'}</TableCell>
                            <TableCell sx={NUM}>{r.fieldAllowance ? fmt(r.fieldAllowance) : '—'}</TableCell>
                            <TableCell sx={NUM}>{r.allowance ? fmt(r.allowance) : '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={e.isFulltime ? 2 : 4} sx={{ ...CELL, fontWeight: 700 }}>รวมวันที่ลงโครงการนี้ ({days.length} วัน)</TableCell>
                        {!e.isFulltime && <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(pr.salary)}</TableCell>}
                        <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(pr.ot)}</TableCell>
                        <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(pr.fa)}</TableCell>
                        <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(pr.allowance)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Line
                label="ค่าแรง"
                formula={e.isFulltime
                  ? `ค่าแรงเต็มเดือน ${fmt(e.actualSalary)} × ${pct(pr.share)} (ลงโครงการนี้ ${fmt(pr.ft?.hours || 0)} จาก ${fmt(e.ftSplit?.totalHours || 0)} ชม.)`
                  : `รวมจากวันที่ลงเวลาที่โครงการนี้ ${pr.daysInProject ?? days.length} วัน`}
                value={pr.salary}
                sign="+"
              />
              <Line label="OT + ค่าออกสนาม + เบี้ยเลี้ยง" formula="ของวันที่ลงเวลาที่โครงการนี้" value={pr.ot + pr.fa + pr.allowance} sign="+" />
              <Line
                label="ส่วนที่ไม่ผูกวัน (แบ่งมา)"
                formula={`(เงินได้อื่นๆ − ปกส. − ภาษี ± ส่วนต่าง/ยอดยกมา) ส่วนที่เหลือรวม ${fmt(e.remainder)} × สัดส่วน ${pct(pr.share)} = ${fmt(remainderPart)}\nเงินได้อื่นๆ ${fmt(pr.bonus)} · ปกส. ${fmt(pr.sso)} · ภาษี ${fmt(pr.tax)}`}
                value={pr.bonus - pr.sso - pr.tax}
                sign={pr.bonus - pr.sso - pr.tax >= 0 ? '+' : ''}
              />
              {(pr.crl > 0 || pr.other > 0) && (
                <Line
                  label="หัก CRL / หักอื่นๆ"
                  formula={e.isFulltime ? 'แบ่งตาม % ชั่วโมง' : 'หักเต็มก้อนที่โครงการนี้ (ลงเวลามากสุดของคนนี้)'}
                  value={pr.crl + pr.other}
                  sign="−"
                  color="#b91c1c"
                />
              )}
              {Math.abs(pr.adj) > 0.005 && (
                <Line label="ส่วนต่าง / ยอดยกมา" formula="HR แก้ตัวเลขในตารางต่างจากยอดรายวัน และยอดยกมา/ยกไปงวดอื่น" value={pr.adj} sign={pr.adj >= 0 ? '+' : ''} />
              )}
              <Line label="ที่ต้องจ่ายของคนนี้ในโครงการนี้" formula="" value={pr.payable} bold color="#c2410c" />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

function ProjectsOverview({ data, query }) {
  const q = query.trim().toLowerCase();
  const list = data.projectsOverview
    .filter((p) => !q || `${p.number} ${p.name}`.toLowerCase().includes(q) || p.list.some((x) => x.name.toLowerCase().includes(q)));
  const COLS = [['salary', 'ค่าแรง'], ['ot', 'OT'], ['fa', 'ออกสนาม'], ['allowance', 'เบี้ยเลี้ยง'], ['bonus', '+เงินได้อื่นๆ'], ['sso', '−ปกส.'], ['tax', '−ภาษี'], ['crl', '−CRL'], ['other', '−หักอื่นๆ']];
  return (
    <Stack spacing={1.5}>
      {list.map((p) => (
        <Box key={p.key} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
          <Stack direction="row" justifyContent="space-between" sx={{ px: 1.5, py: 1, bgcolor: '#fff7ed' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>{p.number || 'ไม่ระบุโครงการ'} {p.name && <span style={{ fontWeight: 400, color: '#64748b' }}>· {p.name}</span>}</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#c2410c' }}>ที่ต้องจ่ายรวม {fmt(p.totalPayable)}</Typography>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD}>พนักงาน</TableCell>
                  {COLS.map(([k, l]) => <TableCell key={k} sx={{ ...HEAD, textAlign: 'right' }}>{l}</TableCell>)}
                  <TableCell sx={{ ...HEAD, textAlign: 'right' }}>ที่ต้องจ่าย</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {p.list.map((x) => (
                  <TableRow key={x.id} hover>
                    <TableCell sx={CELL}>{x.name} <span style={{ color: '#94a3b8' }}>({x.empType === 'FULLTIME' ? 'รายเดือน' : 'รายวัน'})</span></TableCell>
                    {COLS.map(([k]) => <TableCell key={k} sx={NUM}>{Math.abs(x[k] || 0) < 0.005 ? '—' : fmt(x[k])}</TableCell>)}
                    <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(x.payable)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ ...CELL, fontWeight: 700 }}>รวม</TableCell>
                  {COLS.map(([k]) => <TableCell key={k} sx={{ ...NUM, fontWeight: 700 }}>{fmt(p.list.reduce((s, x) => s + (x[k] || 0), 0))}</TableCell>)}
                  <TableCell sx={{ ...NUM, fontWeight: 700 }}>{fmt(p.list.reduce((s, x) => s + x.payable, 0))}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
      {list.length === 0 && <Typography sx={FORMULA}>ไม่พบโครงการ</Typography>}
    </Stack>
  );
}

// dialog "สูตรคำนวณค่าแรง" — อธิบายทุกขั้นตอนของทุกคนในงวดที่เลือก (ดูอย่างเดียว ไม่แก้ข้อมูล)
// focus = { key, external, number } → โชว์เฉพาะโครงการนั้น (เปิดจากปุ่มสูตรในแถวโครงการของตารางต้นทุน) · onClearFocus = กลับไปดูทั้งหมด
// focusEmployeeId = โชว์เฉพาะพนักงานคนนั้น (เปิดจากไอคอนสูตรในแถวพนักงาน) · onClearEmployee = กลับไปดูทั้งหมด
export default function PayrollFormulaDialog({
  open, onClose, data, periodLabel, focus, onClearFocus, focusEmployeeId, onClearEmployee,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});

  const focusEmp = useMemo(
    () => (focusEmployeeId ? (data?.employees || []).find((x) => x.id === String(focusEmployeeId)) || null : null),
    [data, focusEmployeeId],
  );

  const employees = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.employees || []).filter((e) => !q || e.name.toLowerCase().includes(q));
  }, [data, query]);

  const setAll = (v) => {
    const next = {};
    employees.forEach((e) => { next[e.id] = v; });
    setExpanded(next);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2 } }}>
      <Stack direction="row" alignItems="flex-start" sx={{ px: 3, pt: 2, pb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.82rem', color: '#78716c' }}>
            {focusEmp ? 'สูตรคำนวณค่าแรงของพนักงาน' : focus ? 'สูตรคำนวณค่าแรงของโครงการ' : 'สูตรคำนวณค่าแรง'}
          </Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
            {focusEmp ? `${focusEmp.name} · ` : ''}
            {!focusEmp && focus ? `${focus.number || 'ไม่ระบุโครงการ'}${focus.external ? ' (ภายนอก)' : ''} · ` : ''}
            {periodLabel}
          </Typography>
        </Box>
        {focus && !focusEmp && onClearFocus && <Button size="small" onClick={onClearFocus} sx={{ mr: 1 }}>ดูทุกคนทุกโครงการ</Button>}
        {focusEmp && onClearEmployee && <Button size="small" onClick={onClearEmployee} sx={{ mr: 1 }}>ดูทุกคนทุกโครงการ</Button>}
        <IconButton onClick={onClose} aria-label="ปิด"><CloseIcon /></IconButton>
      </Stack>
      <DialogContent dividers sx={{ bgcolor: '#fff' }}>
        {data && focusEmp && (
          <>
            <RulesCard isWorkTime={data.isWorkTime} />
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Chip size="small" label={focusEmp.isFulltime ? 'รายเดือน' : 'รายวัน'} sx={{ height: 20, fontSize: '0.7rem' }} />
              <Typography sx={FORMULA}>ค่าแรง/วัน {fmt(focusEmp.spd)}</Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ fontWeight: 800, color: '#c2410c' }}>ที่ต้องจ่าย {fmt(focusEmp.payable)}</Typography>
            </Stack>
            <EmployeeBody e={focusEmp} />
          </>
        )}
        {data && !focusEmp && focus && (
          <>
            <RulesCard isWorkTime={data.isWorkTime} />
            <ProjectFocus data={data} focus={focus} />
          </>
        )}
        {data && !focusEmp && !focus && (
          <>
            <RulesCard isWorkTime={data.isWorkTime} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
              <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ minHeight: 36, flex: 1 }}>
                <Tab label={`รายคน (${data.employees.length})`} sx={{ minHeight: 36, py: 0 }} />
                <Tab label={`ภาพรวมรายโครงการ (${data.projectsOverview.length})`} sx={{ minHeight: 36, py: 0 }} />
              </Tabs>
              <TextField
                size="small"
                placeholder={tab === 0 ? 'ค้นหาชื่อพนักงาน' : 'ค้นหาโครงการ/พนักงาน'}
                value={query}
                onChange={(ev) => setQuery(ev.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ minWidth: 240 }}
              />
              {tab === 0 && (
                <>
                  <Button size="small" onClick={() => setAll(true)}>ขยายทั้งหมด</Button>
                  <Button size="small" onClick={() => setAll(false)}>ย่อทั้งหมด</Button>
                </>
              )}
            </Stack>

            {tab === 0 && employees.map((e) => (
              <Accordion
                key={e.id}
                expanded={!!expanded[e.id]}
                onChange={(_ev, v) => setExpanded((prev) => ({ ...prev, [e.id]: v }))}
                disableGutters
                TransitionProps={{ unmountOnExit: true }}
                sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', '&:before': { display: 'none' }, mb: 1, borderRadius: 1.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', pr: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', minWidth: 200 }}>{e.name}</Typography>
                    <Chip size="small" label={e.isFulltime ? 'รายเดือน' : 'รายวัน'} sx={{ height: 20, fontSize: '0.7rem' }} />
                    <Typography sx={FORMULA}>ค่าแรง/วัน {fmt(e.spd)}</Typography>
                    <Typography sx={FORMULA}>{e.projects.length} โครงการ</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontWeight: 800, color: '#c2410c', fontSize: '0.92rem' }}>ที่ต้องจ่าย {fmt(e.payable)}</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ borderTop: '1px solid #f1f5f9' }}>
                  <EmployeeBody e={e} />
                </AccordionDetails>
              </Accordion>
            ))}
            {tab === 0 && employees.length === 0 && <Typography sx={FORMULA}>ไม่พบพนักงาน</Typography>}
            {tab === 1 && <ProjectsOverview data={data} query={query} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
